import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft } from "lucide-react";
import { 
  componentTypes, 
  complexityLevels, 
  getEstimatedPoints, 
  getExamplesForType, 
  getManDaysFromPoints,
  getComponentTooltip 
} from "@/data/estimationData";
import { ComponentType, ComplexityLevel, EstimationResult } from "@/types/estimation-types";
import { ComponentExamples } from "./ComponentExamples";
import { toast } from "@/hooks/use-toast";
import { ThemeToggle } from "./ThemeToggle";
import { FibonacciScale } from "./FibonacciScale";
import { EstimationResources } from "./EstimationResources";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import ChatWidget from "./chat/ChatWidget";

interface EstimationToolProps {
  onBackToIntro: () => void;
}

const EstimationTool: React.FC<EstimationToolProps> = ({
  onBackToIntro
}) => {
  const [selectedComponentType, setSelectedComponentType] = useState<ComponentType | "">("");
  const [selectedComplexity, setSelectedComplexity] = useState<ComplexityLevel | "">("");
  const [estimationResult, setEstimationResult] = useState<EstimationResult | null>(null);
  const [activeTab, setActiveTab] = useState<"estimator" | "guidelines" | "resources">("estimator");

  useEffect(() => {
    if (selectedComponentType && selectedComplexity) {
      const storyPoints = getEstimatedPoints(selectedComponentType as ComponentType, selectedComplexity as ComplexityLevel);
      const manDays = getManDaysFromPoints(storyPoints);
      
      setEstimationResult({
        componentType: selectedComponentType as ComponentType,
        complexity: selectedComplexity as ComplexityLevel,
        points: storyPoints,
        manDays
      });
      
      console.log("Updated estimation:", { componentType: selectedComponentType, complexity: selectedComplexity, points: storyPoints, manDays });
    } else {
      setEstimationResult(null);
    }
  }, [selectedComponentType, selectedComplexity]);

  return (
    <div className="container max-w-5xl py-8 space-y-6 animate-fade-in bg-gradient-to-br from-indigo-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="flex items-center justify-between">
        <div className="flex gap-3 items-center">
          <Button variant="outline" size="sm" onClick={onBackToIntro} className="border-blue-200 dark:border-gray-700 hover:bg-blue-100 dark:hover:bg-gray-700">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Intro
          </Button>
          <ThemeToggle />
        </div>
        <div className="flex items-center">
          <img alt="Cardinality Logo" className="h-10 mr-3 hidden md:block" src="/lovable-uploads/41b570c8-bbdf-405b-96af-d22e664c02cd.png" />
          <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-700 dark:from-gray-300 dark:to-gray-400 bg-clip-text text-transparent truncate">Cardy SP and Effort Estimator</h1>
        </div>
      </div>

      <Tabs defaultValue="estimator" className="w-full" onValueChange={value => setActiveTab(value as "estimator" | "guidelines" | "resources")}>
        <TabsList className="grid w-full grid-cols-3 bg-blue-100 dark:bg-gray-700">
          <TabsTrigger value="estimator" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white dark:data-[state=active]:bg-gray-600">
            Estimator
          </TabsTrigger>
          <TabsTrigger value="guidelines" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white dark:data-[state=active]:bg-gray-600">
            Guidelines
          </TabsTrigger>
          <TabsTrigger value="resources" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white dark:data-[state=active]:bg-gray-600">
            Resources
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="estimator" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-1">
              <Card className="bg-white dark:bg-gray-900 border-blue-100 dark:border-gray-800">
                <CardHeader className="border-b border-blue-100 dark:border-gray-800 my-px">
                  <CardTitle className="text-xl text-blue-700 dark:text-gray-300 text-center">Story Point Calculator</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="component-type" className="text-sm font-medium text-blue-900 dark:text-gray-300">
                        Component Type:
                      </label>
                      <Select value={selectedComponentType} onValueChange={value => setSelectedComponentType(value as ComponentType)}>
                        <SelectTrigger id="component-type" className="border-blue-200 dark:border-gray-700">
                          <SelectValue placeholder="Select component type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-800">
                          {componentTypes.map(type => (
                            <TooltipProvider key={type}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SelectItem value={type}>
                                    {type}
                                  </SelectItem>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  {getComponentTooltip(type)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="complexity-level" className="text-sm font-medium text-blue-900 dark:text-gray-300">
                        Complexity Level:
                      </label>
                      <Select value={selectedComplexity} onValueChange={value => setSelectedComplexity(value as ComplexityLevel)}>
                        <SelectTrigger id="complexity-level" className="border-blue-200 dark:border-gray-700">
                          <SelectValue placeholder="Select complexity level" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-800">
                          {complexityLevels.map(level => <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {selectedComponentType && <div className="pt-4 border-t border-blue-100 dark:border-gray-800 mt-4">
                      <h3 className="text-sm font-medium mb-2 text-blue-800 dark:text-gray-300">Examples for {selectedComponentType}:</h3>
                      <ComponentExamples examples={getExamplesForType(selectedComponentType as ComponentType)} selectedComplexity={selectedComplexity as ComplexityLevel} onSelectComplexity={complexity => setSelectedComplexity(complexity)} />
                    </div>}
                </CardContent>
                <CardFooter className="flex justify-between border-t border-blue-100 dark:border-gray-800">
                  <Button variant="outline" className="border-blue-200 text-blue-700 dark:border-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-800" onClick={() => {
                  setSelectedComponentType("");
                  setSelectedComplexity("");
                  setEstimationResult(null);
                }}>
                    Reset
                  </Button>
                </CardFooter>
              </Card>
            </div>

            <div className="md:col-span-1">
              {estimationResult ? <Card className="bg-gradient-to-br from-blue-600 to-blue-700 dark:from-gray-700 dark:to-gray-800 text-white shadow-lg overflow-hidden mb-4">
                  <CardHeader className="border-b border-blue-400 dark:border-gray-600 pb-3">
                    <CardTitle className="text-lg">Estimates</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 text-center">
                    <div className="space-y-4">
                      <div>
                        <div className="text-sm opacity-80">Story Points</div>
                        <div className="text-5xl font-bold mb-1">{estimationResult.points}</div>
                      </div>
                      <div>
                        <div className="text-sm opacity-80">Total Effort</div>
                        <div className="text-xl font-bold mb-1">{estimationResult.manDays} {estimationResult.manDays === 1 ? "day" : "days"}</div>
                      </div>
                      <div>
                        <div className="text-sm opacity-80">Dev Effort</div>
                        <div className="text-xl font-bold mb-1">{Math.round(estimationResult.manDays * 0.4 * 10) / 10} {Math.round(estimationResult.manDays * 0.4) === 1 ? "day" : "days"}</div>
                      </div>
                    </div>
                    <div className="text-blue-100 dark:text-gray-300 text-sm mt-3">
                      {estimationResult.componentType}: {estimationResult.complexity}
                    </div>
                  </CardContent>
                  <div className="bg-blue-500/40 dark:bg-gray-600/40 p-3 mt-2 text-xs text-blue-50 dark:text-gray-200">
                    <p>* Estimates include everything from discovery to SIT, including testing and bug fixing. Pure development effort is approximately 40% of the total estimate.</p>
                  </div>
                </Card> : <Card className="bg-white dark:bg-gray-900 mb-4 border-blue-100 dark:border-gray-800 flex flex-col justify-center items-center p-6 text-center">
                  <div className="text-blue-400 dark:text-gray-500 mb-3 text-lg">
                    Estimate Story Points
                  </div>
                </Card>}

              <div>
                <FibonacciScale />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="guidelines" className="pt-4">
          <Card className="bg-white dark:bg-gray-900 border-blue-100 dark:border-gray-800">
            <CardHeader>
              <CardTitle className="text-xl text-blue-700 dark:text-gray-300">Estimation Guidelines</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-2 text-blue-800 dark:text-gray-300">Story Point Estimation</h3>
                  <p className="text-gray-700 dark:text-gray-400">
                    Story points follow a Fibonacci sequence (1, 2, 3, 5, 8, 13, 21) to reflect
                    the increasing complexity and uncertainty of work items.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2 text-blue-800 dark:text-gray-300">Consider Multiple Factors</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="border-blue-200 dark:border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-md text-blue-700 dark:text-gray-300">Complexity</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-700 dark:text-gray-400">
                          Technical difficulty, domain knowledge required, and architectural impacts.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-blue-200 dark:border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-md text-blue-700 dark:text-gray-300">Effort</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-700 dark:text-gray-400">
                          Amount of work required to complete the task, including testing and documentation.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-blue-200 dark:border-gray-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-md text-blue-700 dark:text-gray-300">Uncertainty</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-700 dark:text-gray-400">
                          Unknown factors, dependencies, and potential risks.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2 text-blue-800 dark:text-gray-300">Use Relative Sizing</h3>
                  <p className="text-gray-700 dark:text-gray-400">
                    Compare stories to previously estimated ones. If feature X was 5 points
                    and this one is more complex, it might be 8 points.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2 text-blue-800 dark:text-gray-300">Story Point Benefits</h3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-400">
                    <li><span className="font-medium">Team Velocity:</span> Story points help teams measure their velocity and improve sprint planning accuracy over time.</li>
                    <li><span className="font-medium">Relative Estimation:</span> Points focus on relative size rather than absolute time, which is often more accurate for complex work.</li>
                    <li><span className="font-medium">Team Alignment:</span> The discussion around points builds shared understanding of the work among team members.</li>
                    <li><span className="font-medium">Uncertainty Handling:</span> The scale naturally accounts for increasing uncertainty in larger items.</li>
                  </ul>
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-medium mb-2 text-blue-800 dark:text-gray-300">Selection Guidelines with Examples</h3>
                  
                  <div className="space-y-6 mt-4">
                    <div>
                      <h4 className="font-medium mb-2 text-blue-700 dark:text-blue-300">Pages/Screens</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                        <li><span className="font-medium">Simple (1 pt / 0.5 day):</span> Single entity, basic layout (e.g. Provider Info page)</li>
                        <li><span className="font-medium">Medium (3 pts / 2 days):</span> Parent-child layout, 2–3 entities (e.g. Youth and Household form)</li>
                        <li><span className="font-medium">Complex (5 pts / 3 days):</span> Tabs, conditional sections, ADA compliance (e.g. Service Plan page with dynamic visibility)</li>
                        <li><span className="font-medium">Supercomplex (8 pts / 5 days):</span> Modals, nested relationships, AI autofill (e.g. Risk Score dashboard)</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2 text-blue-700 dark:text-blue-300">Workflow</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                        <li><span className="font-medium">Simple (3 pts / 2 days):</span> Straight path (e.g. Submit Intake → Assign)</li>
                        <li><span className="font-medium">Medium (5 pts / 3 days):</span> Conditional routing (e.g. Supervisor approval path)</li>
                        <li><span className="font-medium">Complex (13 pts / 8 days):</span> Multi-stage with retry, escalation (e.g. Safety Plan escalation workflow)</li>
                        <li><span className="font-medium">Supercomplex (21 pts / 11 days):</span> Dynamic routing, loops, AI triggers (e.g. Auto-close cases with LLM validation)</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2 text-blue-700 dark:text-blue-300">API</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                        <li><span className="font-medium">Simple (3 pts / 2 days):</span> Single GET/POST API (e.g. Fetch Provider info)</li>
                        <li><span className="font-medium">Medium (5 pts / 3 days):</span> 2–3 CRUD APIs (e.g. Create/Update Youth, Delete Case)</li>
                        <li><span className="font-medium">Complex (8 pts / 5 days):</span> APIs with security headers, rate limiting, input validation</li>
                        <li><span className="font-medium">Supercomplex (13 pts / 8 days):</span> High-performance, secured APIs with caching, throttling, or external auth mechanisms</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2 text-blue-700 dark:text-blue-300">Integration</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                        <li><span className="font-medium">Simple (3 pts / 2 days):</span> Pull data from single source (e.g. provider directory)</li>
                        <li><span className="font-medium">Medium (5 pts / 3 days):</span> Merge/join data from 2–3 internal systems</li>
                        <li><span className="font-medium">Complex (13 pts / 8 days):</span> Cross-platform joins, transformation logic (e.g. syncing case data with external provider)</li>
                        <li><span className="font-medium">Supercomplex (21 pts / 11 days):</span> Real-time sync, AI-triggered workflows, webhook-based multi-system orchestration</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2 text-blue-700 dark:text-blue-300">Rule Engine</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                        <li><span className="font-medium">Simple (2 pts / 1 day):</span> Basic if/then rules (e.g. Eligibility determination based on age and income thresholds)</li>
                        <li><span className="font-medium">Medium (5 pts / 3 days):</span> Multiple condition rules with AND/OR logic (e.g. Service package determination with dynamic program requirements)</li>
                        <li><span className="font-medium">Complex (8 pts / 5 days):</span> Decision trees with validation and dependencies (e.g. Risk assessment scoring with multiple weighted factors)</li>
                        <li><span className="font-medium">Supercomplex (13 pts / 8 days):</span> AI-assisted rules with machine learning components (e.g. Adaptive fraud detection system with learning capabilities)</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2 text-blue-700 dark:text-blue-300">DB Objects</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                        <li><span className="font-medium">Simple (2 pts / 1 day):</span> Simple SELECT/UPDATE trigger</li>
                        <li><span className="font-medium">Medium (5 pts / 3 days):</span> Functions with input validation, simple logic</li>
                        <li><span className="font-medium">Complex (8 pts / 5 days):</span> Stored proc with joins, conditional logic, error handling</li>
                        <li><span className="font-medium">Supercomplex (13 pts / 8 days):</span> Multi-step SPs with transactions, recursion, and performance optimization</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2 text-blue-700 dark:text-blue-300">Reports/Dashboards</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                        <li><span className="font-medium">Simple (1 pt / 0.5 day):</span> Flat report from one table (e.g. Open Case List)</li>
                        <li><span className="font-medium">Medium (3 pts / 2 days):</span> Filters and drilldown (e.g. Monthly visit count by region)</li>
                        <li><span className="font-medium">Complex (5 pts / 3 days):</span> Cross-source, calculated fields (e.g. Referral rate per provider)</li>
                        <li><span className="font-medium">Supercomplex (8 pts / 5 days):</span> Predictive insights or LLM summaries (e.g. AI-generated placement risk report)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2 text-blue-800 dark:text-gray-300">Effort Conversion</h3>
                  <p className="text-gray-700 dark:text-gray-400 mb-2">
                    Story points are converted to effort days according to the following scale:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700 dark:text-gray-400">
                    <li>1 point = 0.5 day</li>
                    <li>2 points = 1 day</li>
                    <li>3 points = 2 days</li>
                    <li>5 points = 3 days</li>
                    <li>8 points = 5 days</li>
                    <li>13 points = 8 days</li>
                    <li>21 points = 11 days</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2 text-blue-800 dark:text-gray-300">Health and Human Services Context</h3>
                  <p className="text-gray-700 dark:text-gray-400">
                    Consider domain complexities unique to health and human services systems, such as:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700 dark:text-gray-400">
                    <li>Compliance with state and federal regulations</li>
                    <li>Data security for sensitive personal information</li>
                    <li>Integration with external agencies and provider networks</li>
                    <li>Reporting requirements for program funding</li>
                    <li>Workflow variations between regions or jurisdictions</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resources" className="pt-4">
          <EstimationResources />
        </TabsContent>
      </Tabs>
      
      <div className="text-center text-xs text-gray-500 dark:text-gray-400 pt-4">
        © {new Date().getFullYear()} CardyAI.com. All rights reserved. | v1.2.0
      </div>

      <ChatWidget />
    </div>
  );
};

export default EstimationTool;
